import React, { useContext, useState } from 'react';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { Controller, useForm } from 'react-hook-form';
import {
  useTheme,
  useMediaQuery,
  makeStyles,
  TextField,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import { colorSet } from '@components/Provider';
import { RemoveRedEye, VisibilityOff } from '@material-ui/icons';
import {
  CapaErrorCode,
  CapaServiceType,
  useAuthenticateLazyQuery
} from '@generated/graphql';
import { useSnackbar } from '@components/Provider';
import { MyAccountContext } from '../../MyAccountContainer';

interface Props {
  confirmSuccess: () => void;
}

export interface Inputs {
  email: string;
  password: string;
}

const EmailConfirmPasswordContainter = ({ confirmSuccess }: Props) => {
  const { subtitle, input } = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const [isVisibilityPassword, setIsVisibilityPassword] = useState(false);

  const context = useContext(MyAccountContext);

  const { control, handleSubmit, setValue, watch } = useForm<Inputs>();
  const password = watch().password;

  const { enqueueSnackbar } = useSnackbar();

  const [authenticate] = useAuthenticateLazyQuery();

  const onSubmit = async (input: Inputs) => {
    try {
      const res = await authenticate({
        variables: {
          ...input,
          service: CapaServiceType.Quotation
        }
      });

      const result = res.data?.authenticate.result;

      if (result === CapaErrorCode.Success) {
        confirmSuccess();
      } else if (
        result === CapaErrorCode.PasswordIncorrect ||
        CapaErrorCode.ParmeterValidationError
      ) {
        setValue('password', '');
        enqueueSnackbar(
          '비밀번호가 일치하지 않습니다.  다시 확인해주세요. 계정 관련 문의는 고객센터(support@capa.ai)로 연락 주세요.',
          { variant: 'error' }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        '비밀번호가 일치하지 않습니다. 다시 확인해주세요. 계정 관련 문의는 고객센터(support@capa.ai)로 연락 주세요.',
        { variant: 'error' }
      );
    }
  };

  return (
    <>
      <div className={subtitle}>
        <CustomizedTypography
          variant={'h4'}
          color="textPrimary"
          fontWeight="500"
          gutterBottoms={matches ? 0 : 60}
        >
          회원님의 개인정보를 보호하기 위해 비밀번호를 다시 한 번 확인합니다.
        </CustomizedTypography>
      </div>
      <form>
        <div className={input}>
          <div className="input">
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              gutterBottoms={8}
            >
              이메일 *
            </CustomizedTypography>
            <Controller
              defaultValue={context.DecodedaccessTokenEmail ?? ''}
              control={control}
              name="email"
              render={(renderProps) => (
                <TextField
                  variant="outlined"
                  disabled
                  style={{
                    height: 40
                  }}
                  InputProps={{
                    readOnly: true
                  }}
                  {...renderProps}
                />
              )}
            />
          </div>

          <div className="input">
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              gutterBottoms={8}
            >
              비밀번호 *
            </CustomizedTypography>
            <Controller
              defaultValue=""
              control={control}
              name="password"
              rules={{
                required: {
                  value: true,
                  message: '비밀번호를 입력해 주세요.'
                }
              }}
              render={(renderProps) => (
                <TextField
                  variant="outlined"
                  placeholder="비밀번호를 입력해 주세요."
                  type={isVisibilityPassword ? 'text' : 'password'}
                  style={{
                    height: 40
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() =>
                            setIsVisibilityPassword(!isVisibilityPassword)
                          }
                        >
                          {isVisibilityPassword ? (
                            <RemoveRedEye />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  {...renderProps}
                />
              )}
            />
          </div>

          <CustomizedButton
            fullWidth
            size="large"
            variant="contained"
            color="primary"
            type="submit"
            disabled={!password}
            onClick={handleSubmit(onSubmit)}
          >
            비밀번호 확인
          </CustomizedButton>
        </div>
      </form>
    </>
  );
};

export default EmailConfirmPasswordContainter;

const useStyles = makeStyles((theme) => ({
  subtitle: {
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%'
    }
  },
  input: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    maxWidth: 360,
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      marginTop: 60
    },
    margin: '0px auto',
    '& .input': {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      marginBottom: 20,
      '& .MuiInputBase-root': {
        borderRadius: 8
      },
      '& .MuiInputBase-root.Mui-disabled': {
        backgroundColor: colorSet.gray100,
        color: colorSet.gray400,
        '-webkit-text-fill-color': colorSet.gray400
      }
    }
  }
}));
